import type {CSSProperties} from 'react';
import {T} from '@territory/tokens';
import {weight} from '../scale.js';

export type WedgeBv2SegmentedLook = 'bv2seg-scroll' | 'bv2seg-fill';

const BV2_SEG_LOOKS = new Set<string>(['bv2seg-scroll', 'bv2seg-fill']);
export const isWedgeBv2SegmentedLook = (look: string): look is WedgeBv2SegmentedLook => BV2_SEG_LOOKS.has(look);

/* Released brief v2 section tab (prep-brief-v2-kit), off-scale. The scrolling strip tab is 44px high with 18px side padding, a 12px radius,
   an 8px gap to its count chip, 15px semibold type and no shrink. The equal-width tab of the phone strip is 38px high with 2px side padding,
   an 11px radius, centered 13.5px semibold type, no wrap and hidden overflow. Both have no border. The fill, the ink and the glow of the
   tab read the --wedge-bv2-seg-bg, --wedge-bv2-seg-ink and --wedge-bv2-seg-glow roles: the caller sets them per tab (accent fill, the app
   ink and a glow when selected; no fill, the secondary ink and no glow when not). The caller sets the font family. Outside the brief each
   role falls back to a base token. Neither look draws a transparent target (wedgeBv2SegmentedDrawsHit): the scrolling tab is 44px high
   already, and an anchor on the equal-width tab would run 3px past the 38px cell into the strip padding and into its 2px-apart
   neighbors, and would add a positioned box that legacy does not have inside an overflow-hidden cell. */
const role = (name: string, fallback: string): string => `var(--wedge-bv2-${name}, ${fallback})`;
export const wedgeBv2SegmentedDrawsHit = (look: string): boolean => !isWedgeBv2SegmentedLook(look);

export function wedgeBv2SegmentedStyle(look: WedgeBv2SegmentedLook): CSSProperties {
  const common = {
    border: 'none', display: 'inline-flex', alignItems: 'center', fontWeight: weight.semibold, cursor: 'pointer', whiteSpace: 'nowrap',
    background: role('seg-bg', 'transparent'), color: role('seg-ink', T.labelSec), boxShadow: role('seg-glow', 'none'),
  } as const;
  if (look === 'bv2seg-fill') {
    return {...common, height: 38, minWidth: 0, padding: '0 2px', borderRadius: 11, justifyContent: 'center', fontSize: 13.5, overflow: 'hidden'};
  }
  return {...common, height: 44, padding: '0 18px', borderRadius: 12, gap: 8, fontSize: 15, flexShrink: 0};
}
